import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { progressTrackingService } from "../../services/api";
import { BaseContext } from "../../contexts/BaseContext";
import { useNavigate } from "react-router-dom";
import { handleAbort } from "../MainContent/ProgressTracking/utils";

export const MiniProgressTracker = ({ maxJobs = 3, onStatusChange }) => {
  const { deasyUserConfig } = useContext(BaseContext);
  const deasyApiKey = deasyUserConfig?.deasyApiKey;
  const navigate = useNavigate();

  const [runningJobs, setRunningJobs] = useState([]);
  const [isAborting, setIsAborting] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [previousProgress, setPreviousProgress] = useState({});
  const [, setNoProgressCalls] = useState(0);
  const noProgressCountRef = useRef(0);
  const hasInitialProgressRef = useRef(false);

  const pollingTime = 10 * 1000; // 10 seconds
  const gracePeriodTime = 20 * 1000; // 20 seconds
  const maxNoProgressCalls = 4; // Stop polling after 4 consecutive calls with no progress

  const fetchRunningJobs = useCallback(async () => {
    if (!deasyApiKey) return [];

    try {
      const response =
        await progressTrackingService.getProgressTrackers(deasyApiKey);
      const allTrackers = response.data.progress_trackers;

      // Filter for only in_progress trackers and sort by start time (newest first)
      const activeJobs = Object.values(allTrackers)
        .filter(
          (tracker) =>
            tracker?.status === "in_progress" &&
            tracker?.total_progress_increments != null &&
            tracker?.completed_progress_increments != null &&
            tracker.completed_progress_increments <
              tracker.total_progress_increments,
        )
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
        .slice(0, maxJobs);

      setRunningJobs(activeJobs || []);

      // Notify parent component about status change if callback exists
      if (onStatusChange) {
        onStatusChange(activeJobs.length > 0);
      }

      return activeJobs;
    } catch (error) {
      console.error("Failed to fetch running jobs:", error);
      return [];
    }
  }, [deasyApiKey, maxJobs, onStatusChange]);

  const startPolling = useCallback(
    () => {
      if (isPolling) return;

      setIsPolling(true);
      // Reset counter when starting polling
      noProgressCountRef.current = 0;
      hasInitialProgressRef.current = false;

      const interval = setInterval(async () => {
        const jobs = await fetchRunningJobs();

        if (jobs.length === 0) {
          clearInterval(interval);
          setIsPolling(false);
          setInGracePeriod(false);
          setNoProgressCalls(0);
          noProgressCountRef.current = 0;
          setPreviousProgress({});
          return;
        }

        // Check if there's been progress since the last poll
        let hasProgress = false;
        const newProgress = {};

        jobs.forEach((job) => {
          const trackerId = job.tracker_id;
          const currentProgress = job.completed_progress_increments;
          newProgress[trackerId] = currentProgress;

          // Check if we have previous progress data for this tracker
          if (
            previousProgress[trackerId] !== undefined &&
            currentProgress > previousProgress[trackerId]
          ) {
            hasProgress = true;
          }
        });

        // Handle first poll - just store the initial progress values
        if (!hasInitialProgressRef.current) {
          hasInitialProgressRef.current = true;
          setPreviousProgress(newProgress);
          return;
        }

        // Update previous progress for next comparison
        setPreviousProgress(newProgress);

        if (hasProgress) {
          // Reset the counter if we detected progress
          noProgressCountRef.current = 0;
          setNoProgressCalls(0);
        } else {
          // Increment the counter if no progress detected
          noProgressCountRef.current += 1;

          setNoProgressCalls(noProgressCountRef.current);

          // If we've reached the limit, stop polling
          if (noProgressCountRef.current >= maxNoProgressCalls) {
            clearInterval(interval);
            setIsPolling(false);
            console.log(
              `Stopping polling after ${maxNoProgressCalls} calls with no progress`,
            );
            noProgressCountRef.current = 0;
          }
        }
      }, pollingTime);

      setPollingInterval(interval);
      return () => clearInterval(interval);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchRunningJobs, isPolling],
  );

  // Initial check and start polling if needed
  useEffect(() => {
    const initialCheck = async () => {
      const jobs = await fetchRunningJobs();
      if (jobs.length > 0) {
        startPolling();
      }
    };

    initialCheck();

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [fetchRunningJobs, startPolling, pollingInterval]);

  // Function to start grace period and polling
  const startGracePeriod = useCallback(
    async (durationMs = 30000) => {
      setInGracePeriod(true);

      // Start immediate check
      const jobs = await fetchRunningJobs();

      if (jobs.length > 0) {
        // have this wait for 10 seconds before starting polling
        setTimeout(() => {
          startPolling();
        }, pollingTime);
      } else {
        // No jobs found immediately, start grace period after waiting for 20 seconds
        setTimeout(() => {
          const gracePeriodInterval = setInterval(async () => {
            console.log("Checking for new tasks...");
            const newJobs = await fetchRunningJobs();

            if (newJobs.length > 0) {
              // Found jobs during grace period, start regular polling
              clearInterval(gracePeriodInterval);
              startPolling();
            }
          }, 2000); // Check more frequently during grace period

          // End grace period after duration
          setTimeout(() => {
            clearInterval(gracePeriodInterval);
            if (!isPolling) {
              setInGracePeriod(false);
            }
          }, durationMs);
        }, gracePeriodTime);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchRunningJobs, startPolling, isPolling],
  );

  // Expose the startGracePeriod function
  useEffect(() => {
    // Add to window for external access or pass via context/props
    window.startTrackerGracePeriod = startGracePeriod;

    return () => {
      delete window.startTrackerGracePeriod;
    };
  }, [startGracePeriod]);

  if (runningJobs.length === 0) {
    return (
      <div className="px-2 py-2 text-sm text-gray-500 text-left">
        {inGracePeriod ? (
          <div className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Checking for new tasks...
          </div>
        ) : (
          "No active tasks."
        )}
      </div>
    );
  }

  return (
    <div className="w-full px-2 pt-3 pb-2">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center">
          <svg
            className="w-4 h-4 mr-2 text-green-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-300">
            Running Tasks
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {Array.isArray(runningJobs) &&
          runningJobs.map((job) => {
            // Calculate progress safely
            const progressPercent = Math.min(
              100,
              Math.max(
                0,
                Math.round(
                  (job.completed_progress_increments /
                    Math.max(job.total_progress_increments, 1)) *
                    100,
                ),
              ),
            );

            return (
              <div
                key={job.tracker_id}
                className="bg-gray-800 rounded p-2.5 cursor-pointer"
                onClick={() => navigate("/progress-tracking")}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-gray-300">
                    {job.task_type === "auto_create"
                      ? "Suggesting metadata..."
                      : job.task_type === "ocr_data_ingestion"
                        ? "Ingesting files..."
                        : job.task_type === "classify"
                          ? "Classifying files..."
                          : job.task_type}
                  </span>
                  <button
                    onClick={(e) =>
                      handleAbort(
                        job.tracker_id,
                        e,
                        deasyApiKey,
                        setRunningJobs,
                        setIsAborting,
                      )
                    }
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    {isAborting[job.tracker_id] ? "Aborting..." : "Abort"}
                  </button>
                </div>

                <div className="w-full bg-gray-700 rounded-full h-1.5 mb-2">
                  <div
                    className="bg-primary h-1.5 rounded-full"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-gray-400">
                  {job.task_type !== "auto_create" ? (
                    <span>
                      {job.completed_progress_increments} /{" "}
                      {job.total_progress_increments} files
                    </span>
                  ) : (
                    <span></span>
                  )}
                  <span>{progressPercent}%</span>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
